import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import {
  getRecentListings,
  getRecentDelistings,
  getPreviousAlerts,
  getLeaderboardAlerts,
  getIncomingListings,
} from "@/api/crypto-alerts";
import {
  AlertTier,
  CryptoAlertData,
  CryptoAlertsPage,
} from "@crypto-trading/common/browser";

/**
 * Helper to deduplicate alerts by ID across pages
 * This prevents duplicate alerts when new items are added at the top
 * and offset-based pagination causes items to shift between pages
 */
const deduplicateAlerts = <T extends { pages: CryptoAlertsPage[] }>(
  data: T
): T => {
  const seenIds = new Set<string>();
  const deduplicatedPages = data.pages.map((page) => ({
    ...page,
    alerts: page.alerts.filter((alert) => {
      if (seenIds.has(alert.id)) {
        return false;
      }
      seenIds.add(alert.id);
      return true;
    }),
  }));

  return {
    ...data,
    pages: deduplicatedPages,
  };
};

/**
 * Hook to get recent listing alerts
 */
export const useRecentListings = (
  userId: string,
  limit: number = 3,
  enabled: boolean = true,
  tier?: AlertTier | null
) => {
  return useQuery({
    queryKey: ["crypto-alerts", "listings", userId, limit, tier ?? "all"],
    queryFn: async (): Promise<CryptoAlertData[]> => {
      const response = await getRecentListings(userId, {
        limit,
        page: 1,
        tier: tier ?? undefined,
      });
      return response.alerts;
    },
    enabled: enabled && !!userId,
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every minute
  });
};

/**
 * Hook to get recent delisting alerts
 */
export const useRecentDelistings = (
  userId: string,
  limit: number = 3,
  enabled: boolean = true,
  tier?: AlertTier | null
) => {
  return useQuery({
    queryKey: ["crypto-alerts", "delistings", userId, limit, tier ?? "all"],
    queryFn: async (): Promise<CryptoAlertData[]> => {
      const response = await getRecentDelistings(userId, {
        limit,
        page: 1,
        tier: tier ?? undefined,
      });
      return response.alerts;
    },
    enabled: enabled && !!userId,
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every minute
  });
};

/**
 * Hook to get listing alerts with infinite scroll pagination
 */
export const useInfiniteListings = (
  userId: string,
  limit: number = 3,
  enabled: boolean = true,
  tier?: AlertTier | null
) => {
  return useInfiniteQuery({
    queryKey: [
      "crypto-alerts",
      "listings",
      "infinite",
      userId,
      limit,
      tier ?? "all",
    ],
    queryFn: ({ pageParam = 1 }): Promise<CryptoAlertsPage> =>
      getRecentListings(userId, {
        limit,
        page: pageParam,
        tier: tier ?? undefined,
      }),
    enabled: enabled && !!userId,
    getNextPageParam: (lastPage) => {
      if (lastPage.hasMore) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    select: deduplicateAlerts,
    initialPageParam: 1,
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every minute
  });
};

/**
 * Hook to get delisting alerts with infinite scroll pagination
 */
export const useInfiniteDelistings = (
  userId: string,
  limit: number = 3,
  enabled: boolean = true,
  tier?: AlertTier | null
) => {
  return useInfiniteQuery({
    queryKey: [
      "crypto-alerts",
      "delistings",
      "infinite",
      userId,
      limit,
      tier ?? "all",
    ],
    queryFn: ({ pageParam = 1 }): Promise<CryptoAlertsPage> =>
      getRecentDelistings(userId, {
        limit,
        page: pageParam,
        tier: tier ?? undefined,
      }),
    enabled: enabled && !!userId,
    getNextPageParam: (lastPage) => {
      if (lastPage.hasMore) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    select: deduplicateAlerts,
    initialPageParam: 1,
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every minute
  });
};

/**
 * Hook to get leaderboard alerts (last 20 listings + 20 delistings with price)
 */
export const useLeaderboardAlerts = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ["crypto-alerts", "leaderboard"],
    queryFn: getLeaderboardAlerts,
    enabled,
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Hook to get incoming listings (pre-market/roadmap/soon-spot/soon-futures, no price yet)
 */
export const useIncomingListings = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ["crypto-alerts", "incoming-listings"],
    queryFn: getIncomingListings,
    enabled,
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Hook to get previous alerts (listings and delistings) for a currency
 */
export const usePreviousAlerts = (
  userId: string,
  currency: string,
  alertId?: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["crypto-alerts", "previous-alerts", userId, currency, alertId],
    queryFn: () => getPreviousAlerts(userId, currency, alertId),
    enabled: enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
